import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, map, switchMap } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { KickstartService } from '../../../../core/kickstart/kickstart.service';
import { AuthService } from '../../../../core/auth/auth.service';
import { aiConsentBoxNeeded } from '../../../../core/attestations/ai-consent.types';
import {
  KsApplicationDetail,
  KsDocumentType,
  KsOffer,
  hasConfirmedAmount,
} from '../../../../core/kickstart/kickstart.types';
import { EntityService } from '../../../../core/entity/entity.service';
import { EntityDetail } from '../../../../core/entity/entity.types';
import { IconComponent } from '../../../../shared/icon/icon';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import { StageRowComponent } from '../../../../shared/stage-row/stage-row';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../../shared/confirm-dialog/confirm-dialog';
import {
  DebitOrderAccountDialogComponent,
  DebitOrderAccountDialogData,
} from './debit-order-account-dialog';
import { ZarPipe } from '../../../../shared/pipes/zar.pipe';
import { DashPipe } from '../../../../shared/pipes/dash.pipe';
import { AgoPipe } from '../../../../shared/pipes/ago.pipe';
import { DocDropType } from '../../shared/doc-drop/doc-drop';
import { DocUploadPanelComponent, PanelDoc } from '../../shared/doc-upload-panel/doc-upload-panel';
import { AiConsentWithdrawComponent } from '../../../../shared/ai-consent/ai-consent-withdraw/ai-consent-withdraw';
import { AiConsentCheckboxComponent } from '../../../../shared/ai-consent/ai-consent-checkbox/ai-consent-checkbox';
import { DeclarationsPanelComponent } from '../../../../shared/declarations-panel/declarations-panel';
import { KsOfferCardComponent } from '../offer-card/ks-offer-card';
import { KsContextBlockComponent } from '../context-block/ks-context-block';
import { ContextBlocksClientComponent } from '../../shared/context-blocks-client/context-blocks-client';
import { KsOutstandingComponent, progressLine } from './outstanding/ks-outstanding';
import { KsAccountStepComponent } from './account-step/ks-account-step';
import { monthsFor } from '../create/ks-create';
import { FirstLookComponent, firstLookApplies } from '../../../../shared/first-look/first-look';
import { readError } from '../../../../core/http/read-error';

/**
 * Document buckets shown on the draft-edit page — the same light, first-pass
 * set the apply wizard asks for, so the two screens match. ID/surety are
 * collected later if the application progresses.
 */
const DOC_TYPES: DocDropType<KsDocumentType>[] = [
  { value: 'BANK_STATEMENT', label: 'Bank statements' },
  { value: 'FINANCIALS', label: 'Financial statements', hint: 'Annual or management accounts' },
  { value: 'OTHER', label: 'Other supporting documents', hint: 'Anything else relevant' },
];

/**
 * DOC-72 UI-DA-72-01 — until the business's AI consent is given, the upload
 * steps say so plainly instead of offering a drop target (`FC-DET-051`).
 */
export const UPLOADS_NEED_CONSENT =
  "Statements can be uploaded once the business's consent to AI assessment is given.";

/** The three client confirmations that gate activation (BRE-200 KS-OB-06/07/08). */
type ConfirmationKey = 'loan_agreement' | 'debit_order' | 'payout_account';

interface ConfirmationSpec {
  key: ConfirmationKey;
  label: string;
  /** What the client is confirming — never a blanket "I agree". */
  description: string;
  dialogTitle: string;
  dialogMessage: string;
  confirmLabel: string;
  action: string;
}

/**
 * BRE-200 KS-OB-06/07/08. Each is a hard gate: until the client confirms (or
 * Ops records a waiver), the application cannot be activated. Copy names the
 * specific thing being confirmed, per the style guide's consent rule.
 */
const CONFIRMATIONS: readonly ConfirmationSpec[] = [
  {
    // FLOW-367 (DOC-68-B UI-DA-68-B-06): signed outside Flow, recorded by CapX —
    // no client control. The description is replaced by `signingLine()`.
    key: 'loan_agreement',
    label: 'Your documents for signing',
    description: '',
    dialogTitle: '',
    dialogMessage: '',
    confirmLabel: '',
    action: '',
  },
  {
    key: 'debit_order',
    label: 'Debit order mandate',
    // Replaced per offer by `mandateSentence()` — the debit days and the rule
    // for the first debit, from the accepted offer (DOC-62-B §2).
    description: 'Confirm the debit order mandate for your instalments.',
    dialogTitle: 'Confirm the debit order mandate?',
    dialogMessage:
      'You are confirming the debit order mandate CapX will use to collect your ' +
      'instalments on the debit dates of the offer you accepted. We record the date ' +
      'and time of this confirmation.',
    confirmLabel: 'Confirm mandate',
    action: 'Confirm mandate',
  },
  {
    key: 'payout_account',
    label: 'Payout account',
    description: 'Confirm the bank account CapX should pay your funds into.',
    dialogTitle: 'Confirm the payout account?',
    dialogMessage:
      'You are confirming the bank account CapX will pay your funds into. Tell us the ' +
      'bank and the last four digits of the account, and check they are correct. We ' +
      'record the date and time of this confirmation.',
    confirmLabel: 'Confirm account',
    action: 'Confirm account',
  },
];

@Component({
  selector: 'app-ks-detail',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    RouterLink,
    DatePipe,
    IconComponent,
    StatusChipComponent,
    StageRowComponent,
    ZarPipe,
    DashPipe,
    AgoPipe,
    DocUploadPanelComponent,
    DeclarationsPanelComponent,
    AiConsentWithdrawComponent,
    AiConsentCheckboxComponent,
    KsOfferCardComponent,
    KsContextBlockComponent,
    ContextBlocksClientComponent,
    KsOutstandingComponent,
    KsAccountStepComponent,
    FirstLookComponent,
  ],
  templateUrl: './ks-detail.html',
  styleUrl: './ks-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KsDetailComponent implements OnInit {
  private readonly kickstartService = inject(KickstartService);
  private readonly entityService = inject(EntityService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly entityId = this.route.snapshot.paramMap.get('id') ?? '';
  private readonly appId = this.route.snapshot.paramMap.get('appId') ?? '';

  /**
   * FLOW-344 — the bank-statements hint names the route's months (`KS-OB-02a/b`),
   * from the step-1 answer the apply form asks them by; no hint until it is given.
   */
  protected readonly docTypes = computed<DocDropType<KsDocumentType>[]>(() => {
    const months = monthsFor(this.application()?.has_financials_declared ?? null);
    return DOC_TYPES.map((dt) =>
      dt.value === 'BANK_STATEMENT' && months ? { ...dt, hint: `Last ${months} months` } : dt,
    );
  });
  protected readonly application = signal<KsApplicationDetail | null>(null);
  protected readonly hasConfirmedAmount = hasConfirmedAmount;
  protected readonly entity = signal<EntityDetail | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly isSubmitting = signal(false);
  protected readonly isWithdrawing = signal(false);
  private readonly auth = inject(AuthService);

  /**
   * POPIA s72 — consent to AI-assisted assessment. Since FLOW-297 it is the
   * BUSINESS's (`§A6.7.2`) and `ai_consent` on this record is the business's
   * state: a second person at a consented business is not asked, a withdrawal
   * by anyone puts the question again (`aiConsentBoxNeeded`).
   */
  protected readonly aiConsent = signal(false);
  protected readonly showAiConsentBox = computed(() =>
    aiConsentBoxNeeded(this.aiConsentState(), this.auth.hasStandingAiConsent()),
  );
  /** DOC-72 UI-DA-72-01 — no upload while the business's consent is absent or withdrawn. */
  protected readonly uploadsNeedConsent = computed(
    () => this.canCapture() && this.showAiConsentBox(),
  );
  protected readonly UPLOADS_NEED_CONSENT = UPLOADS_NEED_CONSENT;
  protected readonly aiConsentOk = computed(() => !this.showAiConsentBox() || this.aiConsent());
  protected readonly serverError = signal<string | null>(null);

  /**
   * AI-processing consent state (FLOW-290). Null until consent is captured at
   * submit — on a draft there is nothing to withdraw, so the control renders
   * nothing and only the checkbox shows.
   */
  protected readonly aiConsentState = computed(() => this.application()?.ai_consent ?? null);

  /**
   * The withdrawal POST, handed to the shared control as a factory so nothing
   * fires until the client confirms. Stable arrow field: a new identity each
   * render would churn the required function input.
   */
  protected readonly withdrawAiConsent = () => this.kickstartService.withdrawAiConsent(this.appId);

  /**
   * Re-read after a withdrawal and untick the box, so the client is asked
   * afresh rather than shown a ticked checkbox over a withdrawn consent.
   * Re-consent then goes through the ordinary submit path and writes a new row.
   */
  protected onAiConsentWithdrawn(): void {
    this.aiConsent.set(false);
    this.load();
  }

  /** Documents already on the application — seeds the upload panel once. */
  protected readonly initialDocs = computed<PanelDoc<KsDocumentType>[]>(
    () => this.application()?.documents ?? [],
  );

  /** True while the panel has an upload in flight (blocks Submit). Fed by the
   *  panel's inFlightChange output. */
  protected readonly anyInFlight = signal(false);

  // Backend calls the shared upload panel needs, kept as stable arrow fields so
  // the panel's function inputs don't change identity between renders.
  protected readonly uploadDoc = (shortId: string, file: File, docType: KsDocumentType) =>
    this.kickstartService.uploadDocument(shortId, file, docType);
  protected readonly deleteDoc = (shortId: string, docShortId: string) =>
    this.kickstartService.deleteDocument(shortId, docShortId);
  protected readonly fetchDocs = (shortId: string) =>
    this.kickstartService.get(shortId).pipe(map((app) => app.documents));

  /** Show capture form when the applicant can still edit the application. */
  protected readonly canCapture = computed(() => {
    const stage = this.application()?.application_stage;
    return stage === 'DRAFT' || stage === 'MORE_INFO_REQUIRED';
  });

  protected readonly canSubmit = computed(() => {
    const stage = this.application()?.application_stage;
    return stage === 'DRAFT' || stage === 'MORE_INFO_REQUIRED';
  });

  /** A draft is unfinished — drives the resume banner, CTA and withdraw link. */
  protected readonly isDraft = computed(() => this.application()?.application_stage === 'DRAFT');

  /**
   * BRE-200 Screen 6.2: show the onboarding-completeness bar while the
   * application is under review (submitted, not yet active). Pre-submit there's
   * nothing being assessed; once ACTIVE the gate is met by definition, so the
   * bar has served its purpose (BRE-200 §7.3 hands off to the dashboard ladder).
   */
  protected readonly showOnboardingProgress = computed(
    () => this.application()?.ks_status === 'PENDING',
  );
  protected readonly onboardingPercent = computed(
    () => this.application()?.onboarding_progress?.percent ?? 0,
  );

  /**
   * DOC-73 (FLOW-326) — the progress line by the position the back end
   * reports; null where it reports none, and the page then says nothing.
   */
  protected readonly progress = computed(() => {
    const app = this.application();
    return app ? progressLine(app.position ?? null, app.outstanding ?? []) : null;
  });

  /** Re-read after an act on an outstanding item, so the list moves as the back end says. */
  protected onOutstandingChanged(): void {
    this.load();
  }

  // -------------------------------------------------------------------------
  // FLOW-303 — Screen 5.0, the offer. Every figure is the record's; this
  // component works none out (DOC-62 §4.1).
  // -------------------------------------------------------------------------

  protected readonly offer = computed<KsOffer | null>(() => this.application()?.offer ?? null);
  protected readonly offerState = computed(() => this.offer()?.state ?? null);
  /** FLOW-224 (DOC-78) — the first look sits on the page while CapX has the file. */
  protected readonly showFirstLook = computed(() =>
    firstLookApplies(this.application()?.application_stage ?? ''),
  );

  /** A CapX change after acceptance — shown old beside new (DOC-62 §5.2). */
  protected readonly offerChanged = computed(() => {
    const o = this.offer();
    return o?.state === 'OPEN' && !!o.previous?.was_accepted;
  });

  /** "[n] days left", from two days before the lapse (DOC-62 §5.3). */
  protected readonly daysLeft = computed<number | null>(() => {
    const expires = this.offer()?.expires_at;
    if (!expires || this.offerState() !== 'OPEN') return null;
    const days = Math.ceil((new Date(expires).getTime() - Date.now()) / 86_400_000);
    return days <= 2 ? Math.max(days, 0) : null;
  });

  protected readonly isAccepting = signal(false);
  protected readonly showMonthlyForm = signal(false);
  protected readonly isRequestingMonthly = signal(false);
  protected readonly monthlyReason = this.fb.nonNullable.control('', Validators.required);

  /**
   * BRE-200 KS-OB-06/07/08 — Screen 5.1. Reachable ONLY while there is an
   * accepted offer that is still the current one (DOC-62 §6.1, `FC-DET-062`):
   * never before approval, never on an open, changed, lapsed or withdrawn offer.
   */
  protected readonly showConfirmations = computed(
    () => this.application()?.ks_status === 'PENDING' && this.offerState() === 'ACCEPTED',
  );

  /**
   * The mandate step states the debit days and the RULE for the first debit —
   * not one fixed date, and no frequency choice (DOC-62-B §2). Wording
   * provisional. Every figure and date is the accepted offer's.
   */
  protected readonly mandateSentence = computed(() => {
    const o = this.offer();
    if (!o) return '';
    const amount = ZAR.transform(o.instalment_amount);
    const when =
      o.repayment_frequency === 'WEEKLY'
        ? 'weekly, on the 7th, 14th, 21st and last day of each month'
        : `monthly, on the ${o.debit_day}${ordinal(o.debit_day ?? 0)} of each month`;
    // A calendar date, not an instant — formatted in UTC so no offset moves it.
    const first = new Date(o.first_debit_date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
    const rule =
      o.first_debit_kind === 'OPS_SET'
        ? `The first debit is ${first}, unless payout is fewer than seven days before it; ` +
          'then it is the next of those dates at least seven days after payout.'
        : 'The first debit is the first of those dates at least seven days after payout — ' +
          `${first} if we pay out today.`;
    return `${amount} ${when}. ${rule}`;
  });

  /**
   * FLOW-367 (DOC-68-B §6) — the client's signing line, by state and route. The
   * agreement is signed outside Flow; there is no sign link while unlinked.
   */
  protected readonly signingLine = computed(() => {
    const status = this.application()?.signing_status;
    if (!status || status.state === 'PREPARING') {
      return "We're preparing your documents for signing.";
    }
    if (status.state === 'SIGNED') return '';
    const sent = new Date(status.sent_on ?? '').toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const where =
      status.route === 'BY_HAND'
        ? ' Sign them by hand and send us a clear scan of every page.'
        : ' Please check your email.';
    return `Your documents were sent to you for signing on ${sent}.${where}`;
  });

  /** Which confirmation is currently in flight (null = none). */
  protected readonly confirmingKey = signal<ConfirmationKey | null>(null);

  /** The three confirmations, each resolved against the current application. */
  protected readonly confirmations = computed(() => {
    const app = this.application();
    const mandate = this.mandateSentence();
    const signing = this.signingLine();
    return CONFIRMATIONS.map((spec) => ({
      ...spec,
      description:
        spec.key === 'debit_order' && mandate
          ? mandate
          : spec.key === 'loan_agreement'
            ? signing
            : spec.description,
      clientAction: spec.key !== 'loan_agreement',
      done: this.readConfirmed(spec.key, app),
      at: this.readConfirmedAt(spec.key, app),
    }));
  });

  /** DOC-86 — the business's own account step, only where this viewer may answer. */
  protected readonly accountStep = computed(() => {
    const step = this.application()?.business_account_confirmation;
    return step?.may_answer ? step : null;
  });

  /** True once every confirmation is given — flips the card into its done state. */
  protected readonly allConfirmed = computed(
    () =>
      !this.application()?.business_account_confirmation?.owed &&
      this.confirmations().every((c) => c.done),
  );

  /** FLOW-119 — the activated facility: fee, schedule, next instalment. */
  protected readonly isActive = computed(() => this.application()?.ks_status === 'ACTIVE');
  protected readonly facility = computed(() => this.application()?.facility ?? null);

  /**
   * Every instalment is the same except the last, which carries the rounding
   * remainder — so quoting "the" instalment uses the first row, not an average.
   */
  protected readonly instalmentAmount = computed(
    () => this.facility()?.instalments[0]?.amount ?? null,
  );

  protected readonly scheduleColumns = ['sequence', 'due_date', 'amount'] as const;

  private readConfirmed(key: ConfirmationKey, app: KsApplicationDetail | null): boolean {
    if (!app) return false;
    if (key === 'loan_agreement') return app.signing_status?.state === 'SIGNED';
    if (key === 'debit_order') return app.debit_order_confirmed;
    return app.payout_account_confirmed;
  }

  private readConfirmedAt(key: ConfirmationKey, app: KsApplicationDetail | null): string | null {
    if (!app) return null;
    if (key === 'loan_agreement') return app.signing_status?.signed_on ?? null;
    if (key === 'debit_order') return app.debit_order_confirmed_at;
    return app.payout_account_confirmed_at;
  }

  protected readonly captureForm = this.fb.group({
    trading_history_months: this.fb.control<number | null>(null, [Validators.min(0)]),
    monthly_turnover_avg: [''],
    // Cash-flow capture (BRE-056 inputs — "cash flow is king"). Optional; the
    // client fills what they can before submitting.
    monthly_expenses_avg: [''],
    surplus_cash: [''],
    overdraft_amount: [''],
    director_credit_score: this.fb.control<number | null>(null, [Validators.min(0)]),
    // §B11.1g — a preference, shown to the person deciding; never a choice.
    repayment_frequency_preference: this.fb.nonNullable.control<'' | 'WEEKLY' | 'MONTHLY'>(''),
  });

  ngOnInit(): void {
    this.load();
  }

  protected reload(): void {
    this.loadError.set(false);
    this.isLoading.set(true);
    this.load();
  }

  private load(): void {
    forkJoin({
      entity: this.entityService.getEntity(this.entityId),
      application: this.kickstartService.get(this.appId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ entity, application }) => {
          this.entity.set(entity);
          this.application.set(application);
          // The upload panel seeds itself from initialDocs() (the application's
          // documents) once the record's short_id is known.
          // Pre-populate capture form
          this.captureForm.patchValue({
            trading_history_months: null,
            monthly_turnover_avg: '',
            monthly_expenses_avg: '',
            surplus_cash: '',
            overdraft_amount: '',
            director_credit_score: null,
            repayment_frequency_preference: application.repayment_frequency_preference ?? '',
          });
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  protected saveCapture(): void {
    if (this.isSaving()) return;
    this.isSaving.set(true);
    this.serverError.set(null);

    const v = this.captureForm.getRawValue();
    this.kickstartService
      .capture(this.appId, {
        trading_history_months: v.trading_history_months ?? undefined,
        monthly_turnover_avg: v.monthly_turnover_avg ?? undefined,
        monthly_expenses_avg: v.monthly_expenses_avg ?? undefined,
        surplus_cash: v.surplus_cash ?? undefined,
        overdraft_amount: v.overdraft_amount ?? undefined,
        director_credit_score: v.director_credit_score ?? undefined,
        repayment_frequency_preference: v.repayment_frequency_preference,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (app) => {
          this.application.set(app);
          this.isSaving.set(false);
          this.snackBar.open('Details saved.', 'Dismiss', { duration: 3000 });
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isSaving.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  protected submitOrRespond(): void {
    const app = this.application();
    if (!app || this.isSubmitting() || !this.aiConsentOk()) return;
    this.isSubmitting.set(true);
    this.serverError.set(null);

    // A ticked fallback box is an explicit (re-)consent; `true` on the standing
    // path lets the server derive from the sign-up consent. Only the response
    // to a more-info request is sent from here: a DRAFT is finished in the
    // application form (DOC-72, FLOW-331), which asks what submit needs.
    const ticked = this.showAiConsentBox() && this.aiConsent();
    const call$ = this.kickstartService.respond(app.short_id, this.aiConsentOk());

    call$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (ticked) this.auth.markAiConsentGiven();
        this.application.set(res.application);
        this.isSubmitting.set(false);
        this.snackBar.open(res.message, 'Dismiss', { duration: 6000 });
        this.cdr.markForCheck();
      },
      error: (err: unknown) => {
        this.isSubmitting.set(false);
        this.serverError.set(readError(err));
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Record one BRE-200 client confirmation (KS-OB-06/07/08).
   *
   * Behind a ConfirmDialog because each one is a legally meaningful, one-way
   * act — there is no un-sign. The backend is idempotent, so a stray repeat is
   * harmless, but the dialog keeps the click deliberate.
   */
  protected confirm(spec: ConfirmationSpec): void {
    const app = this.application();
    if (!app || this.confirmingKey()) return;

    // Both client confirmations name their account — the bank and the last four,
    // nothing pre-filled (DOC-88 UI-DA-88-01 for the mandate; FLOW-368 item 3 for
    // the payout account) — so each is a form, not a yes/no. The dialog closes
    // only once the confirmation is recorded.
    const isMandate = spec.key === 'debit_order';
    this.dialog
      .open(DebitOrderAccountDialogComponent, {
        data: {
          heading: spec.dialogTitle,
          submitLabel: spec.confirmLabel,
          bankLabel: isMandate ? 'Bank the debit order runs on' : 'Bank the money is paid into',
          message: spec.dialogMessage,
          confirm: (account) =>
            isMandate
              ? this.kickstartService.confirmDebitOrder(app.short_id, account)
              : this.kickstartService.confirmPayoutAccount(app.short_id, account),
        } satisfies DebitOrderAccountDialogData,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (!res) return;
        this.application.set(res.application);
        this.snackBar.open(res.message, 'Dismiss', { duration: 5000 });
        this.cdr.markForCheck();
      });
  }

  /**
   * "Accept offer" — one press accepts (DOC-62 §4.2). Sends the version on
   * screen so a change of terms in between is seen before it is accepted.
   */
  protected acceptOffer(): void {
    const app = this.application();
    const offer = this.offer();
    if (!app || !offer || this.isAccepting()) return;
    this.isAccepting.set(true);
    this.serverError.set(null);
    this.kickstartService
      .acceptOffer(app.short_id, offer.version)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.afterClientAct(res, this.isAccepting),
        error: (err: unknown) => this.onClientActError(err, this.isAccepting),
      });
  }

  /** "Ask to pay monthly instead" — WEEKLY offers only, reason required. */
  protected requestMonthly(): void {
    const app = this.application();
    if (!app || this.monthlyReason.invalid || this.isRequestingMonthly()) return;
    this.isRequestingMonthly.set(true);
    this.serverError.set(null);
    this.kickstartService
      .requestMonthly(app.short_id, this.monthlyReason.value.trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.showMonthlyForm.set(false);
          this.afterClientAct(res, this.isRequestingMonthly);
        },
        error: (err: unknown) => this.onClientActError(err, this.isRequestingMonthly),
      });
  }

  private afterClientAct(
    res: { message: string; application: KsApplicationDetail },
    busy: { set(v: boolean): void },
  ): void {
    this.application.set(res.application);
    busy.set(false);
    this.snackBar.open(res.message, 'Dismiss', { duration: 5000 });
    this.cdr.markForCheck();
  }

  private onClientActError(err: unknown, busy: { set(v: boolean): void }): void {
    busy.set(false);
    this.serverError.set(readError(err));
    // The offer may have changed or lapsed under the client — show what is true.
    this.load();
    this.cdr.markForCheck();
  }

  /**
   * Withdraw (soft-delete) an unfinished draft. The POPIA erasure path for an
   * abandoned application and its uploaded documents. DRAFT-only, enforced again
   * on the backend.
   */
  protected withdrawDraft(): void {
    const app = this.application();
    if (!app || this.isWithdrawing()) return;

    const data: ConfirmDialogData = {
      title: 'Withdraw this application?',
      message:
        'This draft and any documents you uploaded will be removed. ' +
        'This cannot be undone — you can always start a new application later.',
      confirmLabel: 'Withdraw application',
      destructive: true,
    };

    this.dialog
      .open(ConfirmDialogComponent, { data, width: '440px' })
      .afterClosed()
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return [];
          }
          this.isWithdrawing.set(true);
          this.serverError.set(null);
          this.cdr.markForCheck();
          return this.kickstartService.deleteDraft(app.short_id);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Application withdrawn.', 'Dismiss', { duration: 5000 });
          void this.router.navigate(['/entity', this.entityId, 'kickstart']);
        },
        error: (err: unknown) => {
          this.isWithdrawing.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }
}

const ZAR = new ZarPipe();

/** "1st", "2nd", "25th" — for the monthly debit day in the mandate sentence. */
function ordinal(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th';
  return { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] ?? 'th';
}
